import { useQuery } from '@tanstack/react-query';
import { queryOptionsGetFeatures } from '../api/shared/settingsApi';
import { environmentName } from '../configuration';
import { useMigrationVersionPreference } from '../hooks/useMigrationVersionPreference';
import IfFeatureEnabledMSR3, {
  IIfFeatureEnabledMSR3Props,
} from './IfFeatureEnabledMSR3';
import { SingleQueryResults } from './QueryResults/SingleQueryResults';

interface MigrationSwitchProps {
  featureFlags: IIfFeatureEnabledMSR3Props['name'];
  /**
   * Optional UI shown while feature flags are loading. Use for non-navigational
   * fallbacks (e.g. sidebar links). Avoid redirects here — they run before flags resolve.
   */
  loadingFallback?: React.ReactNode;
  newComponent: React.ReactNode;
  oldComponent: React.ReactNode;
}

/**
 * MigrationSwitch is a temporary component used during the MSR3 migration.
 *
 * Production behavior:
 * - Ignores version overrides; always shows the canonical behavior.
 * - Renders the new component if the feature flag is enabled, otherwise renders the old component.
 *
 * Non-production behavior:
 * - If ?v=2 or the session version preference is 2: always renders the old component.
 * - If ?v=3 or the session version preference is 3: always renders the new component.
 * - Otherwise (canonical route): renders the new component if the feature flag is enabled, otherwise renders the old component.
 *
 * @param featureFlags - The feature flag(s) to check (e.g., "msr3.customerApp.homePage.route").
 * @param newComponent - The new MSR3 component to render.
 * @param oldComponent - The old component to render.
 */
export const MigrationSwitch = ({
  featureFlags,
  loadingFallback,
  newComponent,
  oldComponent,
}: MigrationSwitchProps) => {
  const featuresQuery = useQuery(queryOptionsGetFeatures());

  const isProd = environmentName === 'production';
  const selectedVersion = useMigrationVersionPreference({ enabled: !isProd });

  const isV2Request = selectedVersion === '2';
  const isV3Request = selectedVersion === '3';

  // A refetch after a failure has no data, so the query goes back to pending.
  // Keep the error fallback mounted through it: swapping to loadingFallback
  // would unmount oldComponent, and every MigrationSwitch inside it refetches
  // again when it remounts, so the request would repeat without end.
  const hasFailed = featuresQuery.errorUpdateCount > 0;

  return (
    <SingleQueryResults
      errorFallback={() => oldComponent}
      loadingFallback={hasFailed ? oldComponent : loadingFallback}
      query={featuresQuery}
    >
      {() => {
        /**
         * NON-PRODUCTION with version override
         *
         * ?v=2 or session preference 2: always renders the old component.
         * ?v=3 or session preference 3: always renders the new component.
         */
        if (!isProd && isV2Request) return oldComponent;
        if (!isProd && isV3Request) return newComponent;

        /**
         * PRODUCTION and NON-PRODUCTION logic
         *
         * Query parameters are ignored, only canonical behavior
         * Show new if flag enabled, old otherwise
         */
        return (
          <>
            <IfFeatureEnabledMSR3 name={featureFlags} isEnabled="true">
              {newComponent}
            </IfFeatureEnabledMSR3>
            <IfFeatureEnabledMSR3 name={featureFlags} isEnabled="false">
              {oldComponent}
            </IfFeatureEnabledMSR3>
          </>
        );
      }}
    </SingleQueryResults>
  );
};
